import { useEffect, useRef, useState, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Extra viewport margin before mounting (default ~1 screen below). */
  rootMargin?: string;
  /** Skip IO and mount immediately (e.g. hash link to section). */
  force?: boolean;
  /** Placeholder min-height so scroll position stays stable before mount. */
  minHeightClassName?: string;
};

/**
 * Mounts children only after the sentinel nears the viewport — cuts initial DOM, JS, and network work.
 */
export function DeferMountUntilVisible({
  children,
  rootMargin = '280px 0px',
  force = false,
  minHeightClassName = 'min-h-[18rem]',
}: Props) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(force);

  useEffect(() => {
    if (force || visible) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin, threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [force, visible, rootMargin]);

  if (visible) {
    return <>{children}</>;
  }

  return <div ref={sentinelRef} aria-hidden className={`w-full ${minHeightClassName}`} />;
}
